<template>
  <div v-if="isLoading" class="h-8 flex justify-center items-center">
    <lottie :options="loadingAnimationOptions" />
  </div>
  <div v-else>
    <div
      class="flex flex-wrap items-center mb-8 border-b border-truegray-200 pb-6"
    >
      <div class="w-2/3">
        <h6 class="font-nunito text-cw-300 mb-2">Post Title</h6>
        <h3 class="font-montserrat text-cw-900 pr-16">
          {{ config.post.title }}
        </h3>
      </div>
      <div class="w-1/3">
        <h6 class="font-nunito text-cw-300 mb-2">Post Published</h6>
        <h5 class="font-nunito text-cw-900">
          {{ config.post.created_at_ui
          }}<span v-if="!hasAdvancedStats"> *</span>
        </h5>
      </div>
      <div class="w-full mt-2">
        <a
          class="font-nunito text-cw-300 hover:text-cw-alt"
          @click="routeToPost"
        >
          View post >
        </a>
      </div>
    </div>

    <div class="w-full">
      <div
        :class="[
          'grid',
          hasAdvancedStats ? 'grid-cols-3 gap-12 pb-6' : 'grid-cols-2',
        ]"
      >
        <div class="col-span-1 flex flex-col items-center justify-center">
          <h5 class="text-cw-900 text-sm font-nunito pb-2 mb-4">
            Total Student Reach
          </h5>
          <div class="flex space-x-4 items-center mb-6">
            <img
              :style="{ maxWidth: '65px' }"
              src="/img/icons/contributor_dashboard/graduation-cap-gradient.svg"
              alt="Graduation cap icon"
            />

            <h2 class="font-montserrat font-black cw-gradient-text m-0">
              {{ formatNumber(config.post.statistics.audience) }}
            </h2>
          </div>
        </div>

        <div class="col-span-1 flex flex-col items-center justify-center">
          <h5 class="text-cw-900 text-sm font-nunito pb-2 mb-4">
            Total School Shares
          </h5>
          <div class="flex space-x-4 items-center mb-6">
            <img
              :style="{ maxWidth: '125px' }"
              src="/img/icons/contributor_dashboard/share-gradient.svg"
              alt="Shares icon"
            />

            <h2 class="font-montserrat font-black cw-gradient-text m-0">
              {{ formatNumber(config.post.statistics.shares) }}
            </h2>
          </div>
        </div>

        <div
          v-if="hasAdvancedStats"
          class="col-span-1 flex flex-col items-center justify-center"
        >
          <h5 class="text-cw-900 text-sm font-nunito pb-2 mb-4">Total Views</h5>
          <div class="flex space-x-4 items-center mb-6">
            <img
              :style="{ maxWidth: '65px' }"
              src="/img/icons/contributor_dashboard/impressions-gradient.svg"
              alt="Views icon"
            />

            <h2 class="font-montserrat font-black cw-gradient-text m-0">
              {{ formatNumber(totals?.impressions) }}
            </h2>
          </div>
        </div>
      </div>

      <div
        v-if="hasAdvancedStats"
        :class="[
          'grid',
          showLinkClicks ? 'grid-cols-3 gap-12' : 'grid-cols-2',
          { 'border-b border-truegray-200 pb-6': hasSchoolsStats },
        ]"
      >
        <div
          :class="[
            'col-span-1 flex flex-col items-center justify-center',
            { 'pl-12': !showLinkClicks },
          ]"
        >
          <h5 class="text-cw-900 text-sm font-nunito pb-2 mb-4">
            Total Mailed
          </h5>
          <div class="flex space-x-4 items-center mb-6">
            <img
              src="/img/icons/contributor_dashboard/mail-gradient.svg"
              alt="Mail icon"
              :style="{ width: '65px' }"
            />

            <h2 class="font-montserrat font-black cw-gradient-text m-0">
              {{ formatNumber(totals?.mailed) }}
            </h2>
          </div>
        </div>

        <div
          :class="[
            'col-span-1 flex flex-col items-center justify-center',
            { 'pr-12': !showLinkClicks },
          ]"
        >
          <h5 class="text-cw-900 text-sm font-nunito pb-2 mb-4">Total Leads</h5>
          <div class="flex space-x-4 items-center mb-6">
            <img
              src="/img/icons/contributor_dashboard/views-gradient.svg"
              alt="Views icon"
              :style="{ width: '65px' }"
            />

            <h2 class="font-montserrat font-black cw-gradient-text m-0">
              {{ formatNumber(totals?.viewed) }}
            </h2>
          </div>
        </div>

        <div
          v-if="showLinkClicks"
          class="col-span-1 flex flex-col items-center justify-center"
        >
          <h5 class="text-cw-900 text-sm font-nunito pb-2 mb-4">
            Total Button Clicks
          </h5>
          <div class="flex space-x-4 items-center mb-6">
            <img
              src="/img/icons/contributor_dashboard/click-gradient.svg"
              alt="Button clicked icon"
              :style="{ width: '55px' }"
            />

            <h2 class="font-montserrat font-black cw-gradient-text m-0">
              {{ formatNumber(totals?.linkClicked) }}
            </h2>
          </div>
        </div>
      </div>
    </div>

    <!-- School shares -->
    <div
      v-if="shares?.length"
      :class="postSharesOpen ? 'pt-2 pb-4' : 'py-2'"
      class="bg-cw-50 rounded mt-6 px-4 py-2"
    >
      <div
        class="flex items-center justify-between transition-all rounded cursor-pointer group"
        @click="postSharesOpen = !postSharesOpen"
      >
        <div class="flex gap-3">
          <img
            src="/img/icons/contributor_dashboard/share-gradient.svg"
            alt="Shares icon"
            class="w-4 h-4"
          />
          <p class="text-xs">Which schools have shared this post?</p>
        </div>
        <div
          :class="postSharesOpen ? 'rotate-90 text-cw-alt' : ''"
          class="group-hover:text-cw-alt transition-all"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            :class="postSharesOpen ? 'w-4 h-4' : 'w-5 h-5'"
            class="transition-all"
          >
            <path
              fill-rule="evenodd"
              d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clip-rule="evenodd"
            />
            <path
              fill-rule="evenodd"
              d="M4.21 14.77a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>
      <div
        :class="
          postSharesOpen ? 'h-auto pt-6 pb-4 px-4 mt-2' : 'h-0 overflow-hidden'
        "
        class="w-full bg-white rounded"
      >
        <div class="flex items-center">
          <div class="w-1/2 pb-1 mb-4 border-b border-truegray-700">
            <h6 class="font-nunito pl-12">School</h6>
          </div>
          <div class="w-1/4 pb-1 mb-4 border-b border-truegray-700">
            <h6 class="font-nunito pl-8">Share Date</h6>
          </div>
          <div class="w-1/4 pb-1 mb-4 border-b border-truegray-700">
            <h6 class="font-nunito pl-4">Audience</h6>
          </div>
        </div>

        <div class="max-h-56 overflow-y-scroll">
          <div
            v-for="(school, i) in shares"
            :key="i"
            class="flex flex-wrap items-center my-2"
          >
            <div class="w-1/2 flex space-x-4 items-center">
              <img
                v-if="school.logo"
                :src="school.logo"
                class="w-6 text-xs"
                alt="School logo"
              />
              <p
                v-if="school.profile_title"
                class="text-truegray-500 truncate overflow-ellipsis pr-4"
              >
                {{ getProfileTitle(school) }}
              </p>
            </div>
            <div class="w-1/4 flex space-x-4">
              <img
                src="/img/icons/contributor_dashboard/published-gradient.svg"
                alt="published icon"
              />
              <p class="text-truegray-500">{{ school.date }}</p>
            </div>
            <div class="w-1/4 flex space-x-4">
              <img
                src="/img/icons/contributor_dashboard/member-gradient.svg"
                alt="published icon"
              />
              <p class="text-truegray-500">
                {{ formatNumber(school.audience) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- /School shares -->

    <!-- Advanced stats -->
    <template v-if="hasAdvancedStats">
      <!-- Post views (impressions) -->
      <div
        v-if="impressions?.length"
        :class="postImpressionsOpen ? 'pt-2 pb-4' : 'py-2'"
        class="bg-cw-50 rounded mt-6 px-4 py-2"
      >
        <div
          class="flex items-center justify-between transition-all rounded cursor-pointer group"
          @click="postImpressionsOpen = !postImpressionsOpen"
        >
          <div class="flex gap-3">
            <img
              src="/img/icons/contributor_dashboard/impressions-gradient.svg"
              alt="Views icon"
              class="w-4 h-4"
            />
            <p class="text-xs">Views by school:</p>
          </div>
          <div
            :class="postImpressionsOpen ? 'rotate-90 text-cw-alt' : ''"
            class="group-hover:text-cw-alt transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              :class="postImpressionsOpen ? 'w-4 h-4' : 'w-5 h-5'"
              class="transition-all"
            >
              <path
                fill-rule="evenodd"
                d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
              <path
                fill-rule="evenodd"
                d="M4.21 14.77a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
        <div
          :class="
            postImpressionsOpen
              ? 'h-auto pt-6 pb-4 px-4 mt-2'
              : 'h-0 overflow-hidden'
          "
          class="w-full bg-white rounded"
        >
          <div class="flex items-center">
            <div class="w-3/4 pb-1 mb-4 border-b border-truegray-700">
              <h6 class="font-nunito pl-12">School</h6>
            </div>
            <div class="w-1/4 pb-1 mb-4 border-b border-truegray-700">
              <h6 class="font-nunito pl-4">Total views</h6>
            </div>
          </div>

          <div class="max-h-56 overflow-y-scroll">
            <div
              v-for="(school, i) in impressions"
              :key="i"
              class="flex flex-wrap items-center my-2"
            >
              <div class="w-3/4 flex space-x-4 items-center">
                <img
                  v-if="school.logo"
                  :src="school.logo"
                  class="w-6 text-xs"
                  alt="School logo"
                />
                <p
                  v-if="school.profile_title"
                  class="text-truegray-500 truncate overflow-ellipsis pr-4"
                >
                  {{ getProfileTitle(school) }}
                </p>
              </div>
              <div class="w-1/4 flex space-x-4">
                <img
                  src="/img/icons/contributor_dashboard/member-gradient.svg"
                  alt="published icon"
                />
                <p class="text-truegray-500">
                  {{ formatNumber(school.count) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Post views (impressions) -->

      <!-- Post mailed -->
      <div
        v-if="mailed?.length"
        :class="postMailedOpen ? 'pt-2 pb-4' : 'py-2'"
        class="bg-cw-50 rounded mt-6 px-4 py-2"
      >
        <div
          class="flex items-center justify-between transition-all rounded cursor-pointer group"
          @click="postMailedOpen = !postMailedOpen"
        >
          <div class="flex gap-3">
            <img
              src="/img/icons/contributor_dashboard/mail-gradient.svg"
              alt="Mail icon"
              class="w-4 h-4"
            />
            <p class="text-xs">Which schools have mailed this post?</p>
          </div>
          <div
            :class="postMailedOpen ? 'rotate-90 text-cw-alt' : ''"
            class="group-hover:text-cw-alt transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              :class="postMailedOpen ? 'w-4 h-4' : 'w-5 h-5'"
              class="transition-all"
            >
              <path
                fill-rule="evenodd"
                d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
              <path
                fill-rule="evenodd"
                d="M4.21 14.77a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
        <div
          :class="
            postMailedOpen
              ? 'h-auto pt-6 pb-4 px-4 mt-2'
              : 'h-0 overflow-hidden'
          "
          class="w-full bg-white rounded"
        >
          <div class="flex items-center">
            <div class="w-1/2 pb-1 mb-4 border-b border-truegray-700">
              <h6 class="font-nunito pl-12">School</h6>
            </div>
            <div class="w-1/4 pb-1 mb-4 border-b border-truegray-700">
              <h6 class="font-nunito pl-8">Mail Date</h6>
            </div>
            <div class="w-1/4 pb-1 mb-4 border-b border-truegray-700">
              <h6 class="font-nunito pl-4">Audience</h6>
            </div>
          </div>

          <div class="max-h-56 overflow-y-scroll">
            <div
              v-for="(school, i) in mailed"
              :key="i"
              class="flex flex-wrap items-center my-2"
            >
              <div class="w-1/2 flex space-x-4 items-center">
                <img
                  v-if="school.logo"
                  :src="school.logo"
                  class="w-6 text-xs"
                  alt="School logo"
                />
                <p
                  v-if="school.profile_title"
                  class="text-truegray-500 truncate overflow-ellipsis pr-4"
                >
                  {{ getProfileTitle(school) }}
                </p>
              </div>
              <div class="w-1/4 flex space-x-4">
                <img
                  src="/img/icons/contributor_dashboard/published-gradient.svg"
                  alt="published icon"
                />
                <p class="text-truegray-500">{{ school.date }}</p>
              </div>
              <div class="w-1/4 flex space-x-4">
                <img
                  src="/img/icons/contributor_dashboard/member-gradient.svg"
                  alt="published icon"
                />
                <p class="text-truegray-500">
                  {{ formatNumber(school.audience) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Post mailed -->

      <!-- Post views -->
      <div
        v-if="views?.length"
        :class="postViewsOpen ? 'pt-2 pb-4' : 'py-2'"
        class="bg-cw-50 rounded mt-6 px-4 py-2"
      >
        <div
          class="flex items-center justify-between transition-all rounded cursor-pointer group"
          @click="postViewsOpen = !postViewsOpen"
        >
          <div class="flex gap-3">
            <img
              src="/img/icons/contributor_dashboard/views-gradient.svg"
              alt="Views icon"
              class="w-4 h-4"
            />
            <p class="text-xs">Leads by school:</p>
          </div>
          <div
            :class="postViewsOpen ? 'rotate-90 text-cw-alt' : ''"
            class="group-hover:text-cw-alt transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              :class="postViewsOpen ? 'w-4 h-4' : 'w-5 h-5'"
              class="transition-all"
            >
              <path
                fill-rule="evenodd"
                d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
              <path
                fill-rule="evenodd"
                d="M4.21 14.77a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
        <div
          :class="
            postViewsOpen ? 'h-auto pt-6 pb-4 px-4 mt-2' : 'h-0 overflow-hidden'
          "
          class="w-full bg-white rounded"
        >
          <div class="flex items-center">
            <div class="w-3/4 pb-1 mb-4 border-b border-truegray-700">
              <h6 class="font-nunito pl-12">School</h6>
            </div>
            <div class="w-1/4 pb-1 mb-4 border-b border-truegray-700">
              <h6 class="font-nunito pl-4">Total leads</h6>
            </div>
          </div>

          <div class="max-h-56 overflow-y-scroll">
            <div
              v-for="(school, i) in views"
              :key="i"
              class="flex flex-wrap items-center my-2"
            >
              <div class="w-3/4 flex space-x-4 items-center">
                <img
                  v-if="school.logo"
                  :src="school.logo"
                  class="w-6 text-xs"
                  alt="School logo"
                />
                <p
                  v-if="school.profile_title"
                  class="text-truegray-500 truncate overflow-ellipsis pr-4"
                >
                  {{ getProfileTitle(school) }}
                </p>
              </div>
              <div class="w-1/4 flex space-x-4">
                <img
                  src="/img/icons/contributor_dashboard/member-gradient.svg"
                  alt="published icon"
                />
                <p class="text-truegray-500">
                  {{ formatNumber(school.count) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Post views -->

      <!-- Post button clicked -->
      <div
        v-if="linkClicked?.length"
        :class="postLinkClickedOpen ? 'pt-2 pb-4' : 'py-2'"
        class="bg-cw-50 rounded mt-6 px-4 py-2"
      >
        <div
          class="flex items-center justify-between transition-all rounded cursor-pointer group"
          @click="postLinkClickedOpen = !postLinkClickedOpen"
        >
          <div class="flex gap-3">
            <img
              src="/img/icons/contributor_dashboard/click-gradient.svg"
              alt="Clicked icon"
            />
            <p class="text-xs">Post button clicks by school:</p>
          </div>
          <div
            :class="postLinkClickedOpen ? 'rotate-90 text-cw-alt' : ''"
            class="group-hover:text-cw-alt transition-all"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              :class="postLinkClickedOpen ? 'w-4 h-4' : 'w-5 h-5'"
              class="transition-all"
            >
              <path
                fill-rule="evenodd"
                d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
              <path
                fill-rule="evenodd"
                d="M4.21 14.77a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
        <div
          :class="
            postLinkClickedOpen
              ? 'h-auto pt-6 pb-4 px-4 mt-2'
              : 'h-0 overflow-hidden'
          "
          class="w-full bg-white rounded"
        >
          <div class="flex items-center">
            <div class="w-3/4 pb-1 mb-4 border-b border-truegray-700">
              <h6 class="font-nunito pl-12">School</h6>
            </div>
            <div class="w-1/4 pb-1 mb-4 border-b border-truegray-700">
              <h6 class="font-nunito pl-4">Total clicks</h6>
            </div>
          </div>

          <div class="max-h-56 overflow-y-scroll">
            <div
              v-for="(school, i) in linkClicked"
              :key="i"
              class="flex flex-wrap items-center my-2"
            >
              <div class="w-3/4 flex space-x-4 items-center">
                <img
                  v-if="school.logo"
                  :src="school.logo"
                  class="w-6 text-xs"
                  alt="School logo"
                />
                <p
                  v-if="school.profile_title"
                  class="text-truegray-500 truncate overflow-ellipsis pr-4"
                >
                  {{ getProfileTitle(school) }}
                </p>
              </div>
              <div class="w-1/4 flex space-x-4">
                <img
                  src="/img/icons/contributor_dashboard/member-gradient.svg"
                  alt="published icon"
                />
                <p class="text-truegray-500">
                  {{ formatNumber(school.count) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Post button clicked -->
    </template>
    <!-- /Advanced stats -->

    <!-- Untracked -->
    <div v-else class="flex flex-col justify-center items-center gap-1 mt-4">
      <p class="text-xs italic">
        * Tracking of advanced stats began in January 2024.
      </p>
      <p class="text-xs italic">No data available for older posts, sorry!</p>
    </div>
    <!-- /Untracked -->
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useStore } from '../../../../js/store/index.js'
import { useRouter } from 'vue-router/composables'
import { formatNumber } from '../../../../js/util/helper.js'
import * as loadingAnimation from '../../../../shared/components/loading-dark.json'
import { EventBus } from '../../../../js/plugins/event-bus.js'

export default defineComponent({
  name: 'ModalPostStats',

  props: {
    config: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    const store = useStore()
    const router = useRouter()

    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isLoading = ref(false)

    const stats = ref(null)
    const postSharesOpen = ref(false)
    const postMailedOpen = ref(false)
    const postImpressionsOpen = ref(false)
    const postViewsOpen = ref(false)
    const postLinkClickedOpen = ref(false)

    const shares = computed(() => stats.value?.shares)
    const mailed = computed(() => stats.value?.mailed)
    const impressions = computed(() => stats.value?.impressions)
    const views = computed(() => stats.value?.views)
    const linkClicked = computed(() => stats.value?.linkClicked)

    const totals = computed(() => stats.value?.counts)
    const hasSchoolsStats = computed(() =>
      [
        shares.value,
        mailed.value,
        impressions.value,
        views.value,
        linkClicked.value,
      ].some((arr) => arr?.length),
    )
    const hasAdvancedStats = computed(
      () => props.config.post.has_advanced_stats,
    )
    const showLinkClicks = computed(() => props.config.post.link_url)

    const getProfileTitle = (school) => {
      const isTrashed = school.is_trashed
      return `${school.profile_title}${isTrashed ? ' (inactive school)' : ''}`
    }

    const routeToPost = () => {
      EventBus.$emit('showModal', false)
      router.push({
        name: 'ContributorsPost',
        params: { post_id: props.config.post.id },
      })
    }

    const init = async () => {
      isLoading.value = true
      const payload = props.config.post.id
      stats.value = await store.dispatch(
        'contributors/dispatchGetStatsPost',
        payload,
      )

      isLoading.value = false
    }

    onMounted(init)

    return {
      formatNumber,
      loadingAnimationOptions,
      isLoading,

      postSharesOpen,
      postMailedOpen,
      postImpressionsOpen,
      postViewsOpen,
      postLinkClickedOpen,

      shares,
      mailed,
      views,
      impressions,
      linkClicked,
      totals,
      hasSchoolsStats,
      hasAdvancedStats,
      showLinkClicks,

      getProfileTitle,
      routeToPost,
      init,
    }
  },
})
</script>
